/** Cabinets — repris de apps/pro-web pour cohérence des données + accents. */
export type PracticeAccent =
  | 'indigo'
  | 'teal'
  | 'pink'
  | 'yellow'
  | 'sky'
  | 'green'
  | 'violet'
  | 'orange'
  | 'cyan'
  | 'rose'
  | 'emerald'
  | 'slate';

export interface Practice {
  uuid: string;
  name: string;
  city: string;
  accent: PracticeAccent;
}

export interface PracticeAccentTokens {
  primary: string;
  hover: string;
  light: string;
  lighter: string;
}

export const PRACTICE_ACCENTS: Record<PracticeAccent, PracticeAccentTokens> = {
  indigo: {
    primary: '#4f46e5',
    hover: '#4338ca',
    light: '#eef2ff',
    lighter: '#e0e7ff',
  },
  teal: {
    primary: '#0d9488',
    hover: '#0f766e',
    light: '#f0fdfa',
    lighter: '#ccfbf1',
  },
  pink: {
    primary: '#db2777',
    hover: '#be185d',
    light: '#fdf2f8',
    lighter: '#fce7f3',
  },
  yellow: {
    primary: '#ca8a04',
    hover: '#a16207',
    light: '#fefce8',
    lighter: '#fef9c3',
  },
  sky: {
    primary: '#0284c7',
    hover: '#0369a1',
    light: '#f0f9ff',
    lighter: '#e0f2fe',
  },
  green: {
    primary: '#16a34a',
    hover: '#15803d',
    light: '#f0fdf4',
    lighter: '#dcfce7',
  },
  violet: {
    primary: '#7c3aed',
    hover: '#6d28d9',
    light: '#f5f3ff',
    lighter: '#ede9fe',
  },
  orange: {
    primary: '#ea580c',
    hover: '#c2410c',
    light: '#fff7ed',
    lighter: '#ffedd5',
  },
  cyan: {
    primary: '#0891b2',
    hover: '#0e7490',
    light: '#ecfeff',
    lighter: '#cffafe',
  },
  rose: {
    primary: '#e11d48',
    hover: '#be123c',
    light: '#fff1f2',
    lighter: '#ffe4e6',
  },
  emerald: {
    primary: '#059669',
    hover: '#047857',
    light: '#ecfdf5',
    lighter: '#d1fae5',
  },
  slate: {
    primary: '#475569',
    hover: '#334155',
    light: '#f8fafc',
    lighter: '#f1f5f9',
  },
};

export const PRACTICES: Practice[] = [
  {
    uuid: '11111111-1111-4111-8111-111111110001',
    name: 'Saint-Michel',
    city: 'Paris 5e',
    accent: 'indigo',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110002',
    name: 'Bastille',
    city: 'Paris 11e',
    accent: 'teal',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110003',
    name: 'Montparnasse',
    city: 'Paris 14e',
    accent: 'pink',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110004',
    name: 'Neuilly',
    city: 'Neuilly-sur-Seine',
    accent: 'yellow',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110005',
    name: 'Versailles',
    city: 'Versailles',
    accent: 'sky',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110006',
    name: 'Boulogne',
    city: 'Boulogne-Billancourt',
    accent: 'green',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110007',
    name: 'Levallois',
    city: 'Levallois-Perret',
    accent: 'violet',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110008',
    name: 'Saint-Cloud',
    city: 'Saint-Cloud',
    accent: 'orange',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110009',
    name: 'La Défense',
    city: 'Courbevoie',
    accent: 'cyan',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110010',
    name: 'Vincennes',
    city: 'Vincennes',
    accent: 'rose',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110011',
    name: 'Issy',
    city: 'Issy-les-Moulineaux',
    accent: 'emerald',
  },
  {
    uuid: '11111111-1111-4111-8111-111111110012',
    name: 'Opéra',
    city: 'Paris 9e',
    accent: 'slate',
  },
];

const STORAGE_KEY = 'ligam-patient-practice-uuid';

export function getPracticeByUuid(
  uuid: string | null | undefined,
): Practice | undefined {
  if (!uuid) return undefined;
  return PRACTICES.find((p) => p.uuid === uuid);
}

export function loadStoredPracticeUuid(): string {
  if (typeof localStorage === 'undefined') return PRACTICES[0]!.uuid;
  const stored = localStorage.getItem(STORAGE_KEY);
  return getPracticeByUuid(stored)?.uuid ?? PRACTICES[0]!.uuid;
}

export function persistPracticeUuid(uuid: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, uuid);
}

const ACCENT_ORDER: PracticeAccent[] = [
  'indigo',
  'teal',
  'pink',
  'yellow',
  'sky',
  'green',
  'violet',
  'orange',
  'cyan',
  'rose',
  'emerald',
  'slate',
];

export function applyPracticeAccent(accent: PracticeAccent): void {
  if (typeof document === 'undefined') return;
  const t = PRACTICE_ACCENTS[accent];
  const root = document.documentElement;
  root.style.setProperty('--sf-primary', t.primary);
  root.style.setProperty('--sf-primary-hover', t.hover);
  root.style.setProperty('--sf-primary-50', t.light);
  root.style.setProperty('--sf-primary-100', t.lighter);
  root.dataset.practiceAccent = accent;

  // Enfant 1 = accent du cabinet ; enfant 2 = couleur suivante dans la liste.
  const idx = ACCENT_ORDER.indexOf(accent);
  const firstAccent = ACCENT_ORDER[idx >= 0 ? idx : 0]!;
  const secondAccent =
    ACCENT_ORDER[((idx >= 0 ? idx : 0) + 1) % ACCENT_ORDER.length]!;
  const first = PRACTICE_ACCENTS[firstAccent];
  const second = PRACTICE_ACCENTS[secondAccent];
  root.style.setProperty('--child-1-primary', first.primary);
  root.style.setProperty('--child-1-light', first.lighter);
  root.style.setProperty('--child-1-track', first.light);
  root.style.setProperty('--child-2-primary', second.primary);
  root.style.setProperty('--child-2-light', second.lighter);
  root.style.setProperty('--child-2-track', second.light);
}
